import * as B from '@blueprintjs/core';
import * as Sentry from '@sentry/react';
import * as I from 'immutable';
import React from 'react';

import {lookoutsStoppedByMove} from 'app/components/Alerts/alertPolicyUtils';
import List from 'app/components/List';
import {usePushNotification} from 'app/components/Notification';
import {AlertPolicy, ApiFeature} from 'app/modules/Remote/Feature';
import {ApiOrganization} from 'app/modules/Remote/Organization';
import {FeaturesActions} from 'app/providers/FeaturesProvider';
import {useProjects} from 'app/providers/ProjectsProvider';
import * as featureCollectionUtils from 'app/utils/featureCollectionUtils';
import * as featureUtils from 'app/utils/featureUtils';
import {useDefaultLayersBySource} from 'app/utils/organizationUtils';
import * as projectUtils from 'app/utils/projectUtils';

import cs from './MovePropertiesMenu.module.scss';

const lookoutWarning = (stopping: AlertPolicy[], multiple: boolean) =>
  stopping.length
    ? `\n\nMoving will stop ${stopping.length === 1 ? 'this Lookout' : 'these Lookouts'} for the selected ${
        multiple ? 'properties' : 'property'
      }: ${stopping.map((p) => p.name).join(', ')}. Lookouts belong to a portfolio and don't move with a property.`
    : '';

/**
 * Popover-based dropdown to show the list of portfolios a user can move
 * properties to.
 *
 * The children passed to this component will be the button that triggers the
 * popover.
 */
const MovePropertiesMenu: React.FunctionComponent<{
  featureCollectionId: number;
  projectId: string;
  features: I.ImmutableOf<ApiFeature[]>;
  children: JSX.Element;
  moveFeatures: FeaturesActions['moveFeatures'];
  setIsUpdatingFeatures: React.Dispatch<React.SetStateAction<boolean>>;
  organization: I.ImmutableOf<ApiOrganization>;
  alertPolicies: AlertPolicy[];
}> = ({
  featureCollectionId,
  projectId,
  features,
  moveFeatures,
  setIsUpdatingFeatures,
  organization,
  alertPolicies,
  children,
}) => {
  const [projects, featureCollectionsById, actions] = useProjects();
  const pushNotification = usePushNotification();
  const defaultLayersBySource = useDefaultLayersBySource(organization);

  const confirmMoveFeatures = async (pId, pName) => {
    const multiple = features.size > 1;
    const featureIds: number[] = features.map((f) => f!.get('id')).toArray();

    const message = `Are you sure you want to move ${
      multiple ? 'these properties' : 'this property'
    } to the ${pName} portfolio?${lookoutWarning(
      lookoutsStoppedByMove(alertPolicies, featureIds),
      multiple
    )}`;

    const project = projects && projectUtils.findProjectInProjects(projects, pId);

    const newFeatureCollectionId =
      project &&
      featureCollectionUtils
        .findPrimaryFeatureCollection(project, featureCollectionsById)
        ?.get('id');

    // This shouldn't be possible, but if it is alert the user instead of failing silently
    if (!project || !newFeatureCollectionId) {
      window.alert('We weren’t able to move your properties right now. Please try again.');
      Sentry.captureException(
        new Error(
          `Missing project or featureCollectionId: ${project} / ${newFeatureCollectionId}. Cannot move features.`
        )
      );
      return;
    }

    if (confirm(message)) {
      setIsUpdatingFeatures(true);
      try {
        await moveFeatures({
          currentFeatureCollectionId: featureCollectionId,
          newFeatureCollectionId,
          newProjectId: project.get('id'),
          featureIds,
        });

        pushNotification({
          message: `Moved ${multiple ? 'properties' : 'property'} to ${pName}.`,
          autoHideDuration: 3000,
          options: {intent: B.Intent.SUCCESS, icon: 'tick'},
        });
      } catch (e) {
        window.alert('We weren’t able to move your properties right now. Please try again.');
        Sentry.captureException(e);
      } finally {
        setIsUpdatingFeatures(false);
      }
    }
  };

  const createNewPortfolioAndMove = async () => {
    const featureIds: number[] = features.map((f) => f!.get('id')).toArray();
    const numOfFeatures = featureIds.length;

    // A new portfolio has no default vegetation-drop lookout to re-enroll into, so that one stops too.
    const stopping = lookoutsStoppedByMove(alertPolicies, featureIds, true);

    const portfolioName = window
      .prompt(
        `You are about to move ${numOfFeatures} ${numOfFeatures === 1 ? 'property' : 'properties'} to a new portfolio.${lookoutWarning(stopping, numOfFeatures > 1)} \n\nPlease enter a name for the new portfolio. `
      )
      ?.trim();
    if (!portfolioName || !portfolioName.trim()) {
      return;
    }

    setIsUpdatingFeatures(true);
    try {
      const result = await actions.createProject(
        organization,
        portfolioName,
        defaultLayersBySource ??
          (organization.get('defaultLayersBySource').toJS() as Record<string, string[]>),
        []
      );

      if (!result) {
        window.alert("We weren't able to create the portfolio right now. Please try again.");
        Sentry.captureException(
          new Error('createProject returned undefined in MovePropertiesMenu')
        );
        return;
      }

      await moveFeatures({
        currentFeatureCollectionId: featureCollectionId,
        newFeatureCollectionId: result.featureCollectionId,
        newProjectId: result.projectId,
        featureIds,
      });

      pushNotification({
        message: `Moved ${numOfFeatures === 1 ? 'property' : 'properties'} to ${portfolioName}.`,
        autoHideDuration: 3000,
        options: {intent: B.Intent.SUCCESS, icon: 'tick'},
      });
    } catch (e) {
      window.alert("We weren't able to create the portfolio right now. Please try again.");
      Sentry.captureException(e);
    } finally {
      setIsUpdatingFeatures(false);
    }
  };

  const projectListItems = projects
    ? projects
        .map((p) => {
          return {id: p!.get('id'), text: p!.get('name'), isDisabled: p!.get('id') === projectId};
        })
        .sortBy((project) => project?.text, featureUtils.alphanumericSort)
        .toArray()
    : [
        {
          id: 'no-valid',
          text: 'There are no other portfolios to move properties to',
          isDisabled: true,
        },
      ];

  return (
    <B.Popover
      position={B.Position.BOTTOM_RIGHT}
      modifiers={{arrow: {enabled: false}}}
      content={
        <List
          className={cs.menu}
          items={[
            {id: 'move', text: 'Select a portfolio...', isDisabled: true},
            {id: 'sep', text: '', isSeparator: true},
            ...projectListItems,
            {id: 'sep-new', text: '', isSeparator: true},
            {id: 'new-portfolio', text: 'Move to a new portfolio', icon: 'plus'},
          ]}
          onClickItem={({id, text}) => {
            if (id === 'new-portfolio') {
              createNewPortfolioAndMove();
            } else {
              confirmMoveFeatures(id, text as string);
            }
          }}
        />
      }
    >
      {children}
    </B.Popover>
  );
};

export default MovePropertiesMenu;
